import React from "react";
import Section from "../layout/Section";
import Statement from "../ui/Statement";
import MicroLabel from "../ui/MicroLabel";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import styles from "./ExpertiseGrid.module.css";

/* ExpertiseGrid — the olive capability matrix.

   A centred head (micro label + mixed sans / serif Statement) over a 3 x 3 grid
   (nine capabilities, three columns). Every cell opens with a quiet hairline,
   then its index as a micro label, a 28px system icon, the capability as an h3
   and one supporting line. No cards, no boxes, no invented proof — the rules
   and the type do the work.

   Motion: <Reveal> on the cells only, staggered in three steps (one per column).
   The h2 is left alone — HeadingWipe (App.js) owns every "main h2". There is no
   pin, no scroll listener and no rAF in this section. */

// TODO(content): replace with real Appinfigo content
// The title keeps the meaning of the previous one ("Deep engineering depth,
// supporting modern systems"); the <em> is the single serif accent word. Case
// is applied in CSS, so the DOM text stays sentence case for screen readers.
const SECTION = {
  eyebrow: "Capabilities",
  title: (
    <>
      Engineering <em>depth</em> for modern systems
    </>
  )
};

// TODO(content): replace with real Appinfigo content
// `icon` values are names from the shared icon set (src/components/icons/Icon.js).
// Every label names a practice SITE_CONTENT already describes — do not add one
// (e.g. "SRE", "UX research") that has no line in src/constants/siteContent.js.
const CAPABILITIES = [
  {
    id: "cloud-native",
    title: "Cloud-Native Architecture",
    description:
      "We design systems for the cloud from the first commit, so they scale, recover and evolve without re-platforming under pressure.",
    icon: "cloud"
  },
  {
    id: "ai-ml",
    title: "AI & ML Engineering",
    description:
      "Models earn their place only when they change a decision. We build assistants and pipelines that hold up in everyday production use.",
    icon: "ai"
  },
  {
    id: "data-platforms",
    title: "Data Platforms",
    description:
      "Scattered data rarely answers a question. We build foundations that make analytics trustworthy across every team that needs it.",
    icon: "data"
  },
  {
    id: "devops-reliability",
    title: "DevOps & Reliability",
    description:
      "Reliable software is built, not bolted on. We wire delivery, observability and recovery into the way your teams ship every day.",
    icon: "devops"
  },
  {
    id: "mobile",
    title: "Mobile Engineering",
    description:
      "Native-feeling apps respect the device and the user. We craft iOS and Android experiences that stay fast on every screen.",
    icon: "mobile"
  },
  {
    id: "cybersecurity",
    title: "Cybersecurity",
    description:
      "Security works best as a design constraint, not a checklist. We account for risk from the earliest architectural decisions.",
    icon: "security"
  },
  {
    id: "api-integrations",
    title: "API & Integrations",
    description:
      "Most products live inside an ecosystem. We design clean, versioned interfaces that let systems exchange data without friction.",
    icon: "api"
  },
  {
    id: "quality-engineering",
    title: "Quality Engineering",
    description:
      "Confidence comes from coverage you can trust. We automate testing across layers so releases stay calm and predictable.",
    icon: "quality"
  },
  {
    id: "ui-ux-design",
    title: "UI/UX Design",
    description:
      "Design is part of every build, not a handoff. We do the wireframes, flows and design systems, then the engineers who designed them build them.",
    icon: "product"
  }
];

// Reveal stagger: three steps (one per desktop column), then it wraps — every
// row reveals left to right and a ninth cell never waits 8 steps.
const REVEAL_STEPS = 3;
const REVEAL_STEP_MS = 80;

export default function ExpertiseGrid() {
  return (
    <Section tone="olive" size="lg" id="expertise" aria-labelledby="expertise-title">
      <div className={styles.head}>
        <MicroLabel as="p" className={styles.eyebrow}>
          {SECTION.eyebrow}
        </MicroLabel>
        <Statement as="h2" size="h2" align="center" id="expertise-title">
          {SECTION.title}
        </Statement>
      </div>

      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- list-style: none strips list semantics in Safari / VoiceOver; the role restores them */}
      <ul className={styles.grid} role="list">
        {CAPABILITIES.map(({ id, title, description, icon }, index) => (
          <Reveal
            as="li"
            key={id}
            className={styles.item}
            delay={(index % REVEAL_STEPS) * REVEAL_STEP_MS}
          >
            <div className={styles.meta}>
              <MicroLabel className="micro--accent" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </MicroLabel>
              <span className={styles.icon}>
                <Icon name={icon} size={28} />
              </span>
            </div>
            <h3 className={styles.title}>{title}</h3>
            <p className={styles.desc}>{description}</p>
          </Reveal>
        ))}
      </ul>
    </Section>
  );
}
